// React
import { ReactElement } from 'react';
// Modules
import { default as ReactSelect, SingleValue, components, StylesConfig } from 'react-select';
import classNames from 'classnames/bind';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
// Types
import { IconsEnum } from '@utmb/types/icons';
// Styles
import cssStyles from './select.module.scss';

interface SelectProps {
    id: string;
    name: string;
    classNameLabel?: string;
    classNameSelect?: string;
    label?: string;
    placeholder: string;
    value: any;
    defaultValue?: string;
    options: any;
    customStyles?: StylesConfig;
    onChange?: any;
    isClearable?: boolean;
    required?: boolean;
    isMulti?: boolean;
    closeMenuOnSelect?: boolean;
}

const cx = classNames.bind(cssStyles);

export const DropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <Icon variant={IconsEnum.CHEVRON_DOWN} />
        </components.DropdownIndicator>
    );
};

// noinspection JSUnusedGlobalSymbols
export const defaultCustomStyles: StylesConfig = {
    option: (styles, { isFocused, isSelected }) => ({
        ...styles,
        cursor: 'pointer',
        color: 'var(--utmb-color-primary)',
        backgroundColor: isFocused || isSelected ? 'var(--utmb-color-gray-hover) !important' : 'var(--utmb-color-white)',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: 'var(--utmb-color-primary)',
    }),
    dropdownIndicator: (provided, { selectProps }) => ({
        ...provided,
        transform: `rotate(${selectProps.menuIsOpen ? -180 : 0}deg)`,
        transitionDuration: '0.2s',
        transitionProperty: 'transform',
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '0',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export const Select = (props: SelectProps): ReactElement => {
    const {
        id,
        name,
        classNameLabel,
        classNameSelect,
        label,
        placeholder,
        value,
        defaultValue,
        options,
        customStyles,
        onChange,
        isClearable = false,
        required = false,
        isMulti = false,
        closeMenuOnSelect = true,
    } = props;

    const labelCSS = cx(cssStyles.label, classNameLabel);
    const selectCSS = cx(cssStyles.select, classNameSelect);

    const componentStyles = {
        ...defaultCustomStyles,
        ...customStyles,
    };

    return (
        <>
            {label && (
                <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={labelCSS}>
                    {label}
                    {required ? ' *' : ''}
                </Font.OxaniumRegular>
            )}

            <ReactSelect
                id={id}
                name={name}
                className={selectCSS}
                styles={componentStyles}
                components={{ DropdownIndicator }}
                placeholder={placeholder}
                options={options}
                onChange={onChange || null}
                defaultValue={defaultValue}
                value={value}
                isClearable={isClearable}
                isMulti={isMulti}
                menuPortalTarget={document.body}
                closeMenuOnSelect={closeMenuOnSelect}
            />
        </>
    );
};

export type SelectOption = {
    label: string;
    value: string | number | null;
};
